import * as Yup from 'yup';
import { Agreement } from '@economic/react-app';
import { t } from '@lingui/macro';

import { SendByType, SendElectronicInvoiceMethod } from '../book-and-send/types';
import { splitEmails } from '.';
import { yupEconomicSpecificEmail } from '../../../../../shared/util/utilities/isValidEmail';

export function emailOptionsValidationSchema(agreement: Agreement) {
    const schema = Yup.object().shape({
        sendByType: Yup.number().required(),
        emailOptions: Yup.object().when('sendByType', {
            is: Number(SendByType.email),
            then: Yup.object().shape({
                recipients: Yup.string().test('recipients', '', emailOptionsFieldValidation(agreement)),
                recipientsCc: Yup.string().test('recipientsCc', '', emailOptionsFieldValidation(agreement)),
                recipientsBcc: Yup.string().test('recipientsBcc', '', emailOptionsFieldValidation(agreement)),
            }),
        }),
        einvoiceOptions: Yup.object().when('sendByType', {
            is: Number(SendByType.oioxml),
            then: Yup.object().shape({
                glnNumber: Yup.string()
                    .when('sendingMethod', {
                        is: value => value === SendElectronicInvoiceMethod.GlnNumber,
                        then: Yup.string().required(),
                    })
                    .nullable(),

                cvrNumber: Yup.string()
                    .when('sendingMethod', {
                        is: value => value === SendElectronicInvoiceMethod.CvrNumber,
                        then: Yup.string().required(),
                    })
                    .nullable(),
                pNumber: Yup.string()
                    .when('sendingMethod', {
                        is: value => value === SendElectronicInvoiceMethod.PNumber,
                        then: Yup.string().required(),
                    })
                    .nullable(),
            }),
        }),
    });
    return schema;
}

export function emailOptionsFieldValidation(
    agreement: Agreement
): (this: Yup.TestContext, value: any) => boolean | Yup.ValidationError | Promise<boolean | Yup.ValidationError> {
    return async function (emails) {
        const emailValidation = yupEconomicSpecificEmail();
        if (emails) {
            // test if emails are valid
            for (const email of splitEmails(emails)) {
                if (!(await emailValidation.isValid(email))) {
                    return this.createError({
                        message: t`Invalid e-mail address: ${email}`,
                    });
                }
            }

            // test if allowed number of recipients is not exceeded
            const oneDay = 1000 * 60 * 60 * 24; // Milliseconds in one day
            const { recipients, recipientsCc, recipientsBcc } = this.parent;
            const agreementCreationDate = new Date(agreement.subscription.creationDate);
            const agreementAgeInDays = Math.floor((new Date().getTime() - agreementCreationDate.getTime()) / oneDay);
            const isTrialOrNewCompany = agreement.subscription.trial || agreementAgeInDays <= 14;
            const allowedRecipientsCount = isTrialOrNewCompany ? 5 : 20;

            const splitRecipients = splitEmails(recipients);
            const splitRecipientsCc = splitEmails(recipientsCc);
            const splitRecipientsBcc = splitEmails(recipientsBcc);

            if (splitRecipients.length + splitRecipientsCc.length + splitRecipientsBcc.length > allowedRecipientsCount) {
                return this.createError({ message: t`Recipients on emails cannot exceed ${allowedRecipientsCount}` });
            }
        }
        return true;
    };
}
